import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { 
  X,
  Upload,
  FileText,
  DollarSign,
  Calendar,
  User,
  Tag,
  AlertTriangle,
  CheckCircle,
  Save,
  Send,
  Plus,
  Trash2,
  Info,
  Target,
  TrendingUp,
  Clock,
  Shield
} from "lucide-react";

interface GovernanceSubmissionProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (submission: SubmissionData) => void;
}

interface SubmissionData {
  id: string;
  title: string;
  submissionType: "Capital Allocation" | "Strategic Initiative" | "Policy Proposal" | "Partnership Proposal" | "Other";
  customType?: string;
  board: "commercial" | "operational";
  businessJustification: string;
  strategicFit: string[];
  estimatedBudget: number;
  expectedROI?: number;
  riskLevel: "Low" | "Medium" | "High";
  riskExplanation?: string;
  proposedOwner: string;
  startDate: string;
  endDate: string;
  attachments: File[];
  submittedBy: string;
  submittedDate: string;
  status: "Under Review";
}

const strategicFitOptions = [
  "Digitalization",
  "Sustainability",
  "Customer Experience",
  "Operational Excellence",
  "Innovation",
  "Market Expansion",
  "Cost Optimization",
  "Risk Management",
  "Regulatory Compliance",
  "Strategic Partnerships",
  "Technology Advancement",
  "Workforce Development"
];

const mockUsers = [
  { id: "1", name: "John Smith", role: "Director of Operations" },
  { id: "2", name: "Sarah Johnson", role: "VP of Strategy" },
  { id: "3", name: "Michael Chen", role: "Senior Project Manager" },
  { id: "4", name: "Lisa Wang", role: "Technology Lead" },
  { id: "5", name: "Robert Davis", role: "Business Development Manager" },
  { id: "6", name: "Amanda Rodriguez", role: "Finance Director" }
];

export function GovernanceSubmission({ isOpen, onClose, onSubmit }: GovernanceSubmissionProps) {
  const [formData, setFormData] = useState({
    title: "",
    submissionType: "" as SubmissionData["submissionType"] | "",
    customType: "",
    board: "commercial" as "commercial" | "operational",
    businessJustification: "",
    strategicFit: [] as string[],
    estimatedBudget: "",
    expectedROI: "",
    riskLevel: "Medium" as "Low" | "Medium" | "High",
    riskExplanation: "",
    proposedOwner: "",
    startDate: "",
    endDate: ""
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [isDraft, setIsDraft] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.title.trim()) errors.push("Proposal title is required");
    if (!formData.submissionType) errors.push("Submission type is required");
    if (formData.submissionType === "Other" && !formData.customType.trim()) {
      errors.push("Custom submission type is required");
    }
    if (!formData.businessJustification.trim()) errors.push("Business justification is required");
    if (!formData.proposedOwner) errors.push("Proposed owner is required");
    if (!formData.startDate) errors.push("Start date is required");
    if (!formData.endDate) errors.push("End date is required");
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      errors.push("End date must be after start date");
    }

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast.error("Please fix the following errors:", {
        description: errors.join(", ")
      });
      return;
    }

    const submission: SubmissionData = {
      id: `${formData.board.toUpperCase().substring(0, 2)}-${Date.now()}`,
      title: formData.title,
      submissionType: formData.submissionType as SubmissionData["submissionType"],
      customType: formData.submissionType === "Other" ? formData.customType : undefined,
      board: formData.board,
      businessJustification: formData.businessJustification,
      strategicFit: formData.strategicFit,
      estimatedBudget: parseFloat(formData.estimatedBudget) || 0,
      expectedROI: formData.expectedROI ? parseFloat(formData.expectedROI) : undefined,
      riskLevel: formData.riskLevel,
      riskExplanation: formData.riskExplanation,
      proposedOwner: formData.proposedOwner,
      startDate: formData.startDate,
      endDate: formData.endDate,
      attachments: attachments,
      submittedBy: "Current User", // In real app, from auth context
      submittedDate: new Date().toISOString(),
      status: "Under Review"
    };

    onSubmit(submission);
    handleClose();
  };

  const handleSaveDraft = () => {
    setIsDraft(true);
    toast.success("Draft saved successfully", {
      description: "Your submission has been saved to your drafts"
    });
  };

  const handleClose = () => {
    const hasUnsavedChanges = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== ""
    ) || attachments.length > 0;

    if (hasUnsavedChanges && !isDraft) {
      setShowConfirmDialog(true);
    } else {
      // Reset form
      setFormData({
        title: "",
        submissionType: "" as any,
        customType: "",
        board: "commercial",
        businessJustification: "",
        strategicFit: [],
        estimatedBudget: "",
        expectedROI: "",
        riskLevel: "Medium",
        riskExplanation: "",
        proposedOwner: "",
        startDate: "",
        endDate: ""
      });
      setAttachments([]);
      setIsDraft(false);
      onClose();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= 20 * 1024 * 1024; // 20MB
      
      if (!isValidType) {
        toast.error(`${file.name} is not a supported file type`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} exceeds the 20MB size limit`);
        return false;
      }
      return true;
    });

    setAttachments(prev => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleStrategicFitToggle = (option: string) => {
    setFormData(prev => ({
      ...prev,
      strategicFit: prev.strategicFit.includes(option)
        ? prev.strategicFit.filter(item => item !== option)
        : [...prev.strategicFit, option]
    }));
  };

  const filteredUsers = mockUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case "Low": return "bg-green-100 text-green-800 border-green-200";
      case "Medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "High": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Full-screen Modal Overlay */}
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
        <div className="fixed inset-4 bg-background rounded-lg shadow-2xl border">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-2xl font-semibold">New Governance Submission</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Submit strategic proposals for board evaluation and approval
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Board Toggle */}
              <div className="flex items-center gap-2">
                <Label className="text-sm">Board:</Label>
                <Select value={formData.board} onValueChange={(value) => setFormData(prev => ({ ...prev, board: value as "commercial" | "operational" }))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commercial">Commercial Board</SelectItem>
                    <SelectItem value="operational">Operational Board</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="ghost" size="sm" onClick={handleClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Form Content */}
          <ScrollArea className="flex-1 p-6" style={{ height: "calc(100vh - 200px)" }}>
            <div className="max-w-4xl mx-auto space-y-8">
              {/* Proposal Title */}
              <div className="space-y-2">
                <Label htmlFor="title" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Proposal Title *
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Expansion of B2B Ordering Platform"
                  className="text-lg"
                />
              </div>

              {/* Submission Type */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Submission Type *
                </Label>
                <Select value={formData.submissionType} onValueChange={(value) => setFormData(prev => ({ ...prev, submissionType: value as any }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select submission type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Capital Allocation">Capital Allocation</SelectItem>
                    <SelectItem value="Strategic Initiative">Strategic Initiative</SelectItem>
                    <SelectItem value="Policy Proposal">Policy Proposal</SelectItem>
                    <SelectItem value="Partnership Proposal">Partnership Proposal</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.submissionType === "Other" && (
                  <Input
                    value={formData.customType}
                    onChange={(e) => setFormData(prev => ({ ...prev, customType: e.target.value }))}
                    placeholder="Specify custom submission type"
                    className="mt-2"
                  />
                )}
              </div>

              {/* Business Justification */}
              <div className="space-y-2">
                <Label htmlFor="justification" className="flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Business Justification *
                </Label>
                <Textarea
                  id="justification"
                  value={formData.businessJustification}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessJustification: e.target.value }))}
                  placeholder="Describe the strategic importance, urgency, dependencies, and expected business impact..."
                  rows={6}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  Include strategic importance, urgency, or dependencies
                </p>
              </div>

              {/* Strategic Fit */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Strategic Fit
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {strategicFitOptions.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={option}
                        checked={formData.strategicFit.includes(option)}
                        onCheckedChange={() => handleStrategicFitToggle(option)}
                      />
                      <Label htmlFor={option} className="text-sm">
                        {option}
                      </Label>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {formData.strategicFit.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => handleStrategicFitToggle(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Financial Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="budget" className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Estimated Budget (USD)
                  </Label>
                  <Input
                    id="budget"
                    type="number"
                    value={formData.estimatedBudget}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedBudget: e.target.value }))}
                    placeholder="0"
                    min="0"
                    step="1000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roi" className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Expected ROI (%)
                  </Label>
                  <Input
                    id="roi"
                    type="number"
                    value={formData.expectedROI}
                    onChange={(e) => setFormData(prev => ({ ...prev, expectedROI: e.target.value }))}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p className="text-xs text-muted-foreground">Optional - used for decision dashboards</p>
                </div>
              </div>

              {/* Risk Assessment */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Risk Level
                </Label>
                <Select value={formData.riskLevel} onValueChange={(value) => setFormData(prev => ({ ...prev, riskLevel: value as any }))}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        Low Risk
                      </div>
                    </SelectItem>
                    <SelectItem value="Medium">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        Medium Risk
                      </div>
                    </SelectItem>
                    <SelectItem value="High">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        High Risk
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <Badge variant="outline" className={getRiskLevelColor(formData.riskLevel)}>
                  {formData.riskLevel} Risk Level
                </Badge>

                <Textarea
                  value={formData.riskExplanation}
                  onChange={(e) => setFormData(prev => ({ ...prev, riskExplanation: e.target.value }))}
                  placeholder="Optional: Explain the risk factors and mitigation strategies..."
                  rows={3}
                />
              </div>

              {/* Proposed Owner */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Proposed Owner *
                </Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  
                  <ScrollArea className="h-32 border rounded-md p-2">
                    <div className="space-y-1">
                      {filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted ${
                            formData.proposedOwner === user.name ? "bg-muted" : ""
                          }`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, proposedOwner: user.name }));
                            setSearchTerm("");
                          }}
                        >
                          <div>
                            <div className="font-medium text-sm">{user.name}</div>
                            <div className="text-xs text-muted-foreground">{user.role}</div>
                          </div>
                          {formData.proposedOwner === user.name && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  
                  {formData.proposedOwner && (
                    <Alert>
                      <User className="h-4 w-4" />
                      <AlertDescription>
                        Selected: <strong>{formData.proposedOwner}</strong>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Target Start Date *
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate" className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Target End Date *
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>

              {/* Attachments */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Attachments
                </Label>
                
                <div 
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF, DOCX, XLSX files up to 20MB
                  </p>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                />

                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Uploaded Files:</h4>
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          {/* Footer Actions */}
          <div className="flex items-center justify-between p-6 border-t bg-muted/50">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              All required fields must be completed before submission
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleSaveDraft}>
                <Save className="h-4 w-4 mr-2" />
                Save Draft
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-primary">
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard Changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Continue Editing
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                setShowConfirmDialog(false);
                // Reset and close
                setFormData({
                  title: "",
                  submissionType: "" as any,
                  customType: "",
                  board: "commercial",
                  businessJustification: "",
                  strategicFit: [],
                  estimatedBudget: "",
                  expectedROI: "",
                  riskLevel: "Medium",
                  riskExplanation: "",
                  proposedOwner: "",
                  startDate: "",
                  endDate: ""
                });
                setAttachments([]);
                setIsDraft(false);
                onClose();
              }}
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}